import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import BaseChart from "../../components/ui/BaseChart";
import { fetchExpenseYearlyTrend } from "../../services/api/invoiceAnalytics";

const CURRENT_YEAR = new Date().getFullYear();

const compactUsd = (n) => {
  const v = Number(n ?? 0);
  if (Math.abs(v) >= 1000) return `$${(v / 1000).toFixed(1)}k`;
  return `$${v}`;
};

const fullUsd = (n) =>
  `$${Number(n ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const ExpenseYearTrendChart = ({ clientId, role = "FREELANCER", mode = "expense" }) => {
  const isRevenue = mode === "revenue";
  const totalBarLabel = isRevenue ? "Total Revenue" : "Total Expense";

  const { data: result, isLoading } = useQuery({
    queryKey: ["expense-year-trend", CURRENT_YEAR, clientId, role],
    queryFn: () =>
      fetchExpenseYearlyTrend({
        year: CURRENT_YEAR,
        role,
        ...(clientId ? { clientId } : {}),
      }),
    staleTime: 60_000,
  });

  const payload = result?.data;
  const categories = payload?.categories;
  const series = payload?.series;
  const stacked = payload?.stacked;

  const chartOptions = useMemo(() => {
    if (!series?.length) return null;

    // Expense view → stacked columns (engineer expense + company expense), so
    // each month's column is the overall expense with company expense as a band.
    if (stacked) {
      return {
        chart: {
          type: "column",
          height: 400,
          spacing: [12, 12, 8, 8],
          backgroundColor: "transparent",
          style: { fontFamily: "Poppins, sans-serif" },
          animation: { duration: 600 },
        },
        title: { text: null },
        xAxis: {
          categories,
          lineColor: "#e5e7eb",
          tickLength: 0,
          labels: { style: { fontSize: "10px", color: "#9ca3af" } },
          crosshair: { color: "#f1f5f9" },
        },
        yAxis: {
          title: { text: null },
          gridLineColor: "#f3f4f6",
          min: 0,
          labels: {
            formatter() {
              return compactUsd(this.value);
            },
            style: { fontSize: "10px", color: "#9ca3af" },
          },
        },
        legend: {
          align: "center",
          verticalAlign: "bottom",
          layout: "horizontal",
          itemStyle: { fontSize: "10px", fontWeight: "normal", color: "#555" },
          itemHoverStyle: { color: "#111" },
          symbolRadius: 6,
          symbolHeight: 8,
          symbolWidth: 8,
        },
        tooltip: {
          backgroundColor: "#1e293b",
          style: { color: "#f8fafc", fontSize: "11px" },
          borderWidth: 0,
          borderRadius: 6,
          shared: true,
          headerFormat: `<b>{point.key} ${CURRENT_YEAR}</b><br/>`,
          pointFormatter() {
            return `<span style="color:${this.color}">●</span> ${this.series.name}: <b>${fullUsd(this.y)}</b><br/>`;
          },
        },
        plotOptions: {
          column: {
            stacking: "normal",
            borderRadius: 4,
            borderWidth: 0,
            pointPadding: 0.06,
            groupPadding: 0.14,
          },
        },
        series: series.map((s) => ({
          type: "column",
          name: s.name,
          data: s.data,
          color: s.color,
        })),
      };
    }

    // Monthly total across all client lines → background bars.
    // Single total line per month (no per-client breakdown).
    const monthCount = categories?.length ?? 12;
    const monthlyTotals = Array.from({ length: monthCount }, (_, i) =>
      Math.round(series.reduce((sum, s) => sum + (s.data?.[i] ?? 0), 0) * 100) / 100,
    );

    return {
      chart: {
        type: "column",
        height: 400,
        spacing: [12, 12, 8, 8],
        backgroundColor: "transparent",
        style: { fontFamily: "Poppins, sans-serif" },
        animation: { duration: 600 },
      },
      title: { text: null },
      xAxis: {
        categories,
        lineColor: "#e5e7eb",
        tickLength: 0,
        labels: { style: { fontSize: "10px", color: "#9ca3af" } },
        crosshair: { color: "#f1f5f9" },
      },
      yAxis: {
        title: { text: null },
        gridLineColor: "#f3f4f6",
        min: 0,
        labels: {
          formatter() {
            return compactUsd(this.value);
          },
          style: { fontSize: "10px", color: "#9ca3af" },
        },
      },
      legend: {
        align: "center",
        verticalAlign: "bottom",
        layout: "horizontal",
        maxHeight: 78,
        navigation: { activeColor: "#4F46E5", inactiveColor: "#cbd5e1" },
        itemStyle: { fontSize: "10px", fontWeight: "normal", color: "#555" },
        itemHoverStyle: { color: "#111" },
        symbolRadius: 6,
        symbolHeight: 8,
        symbolWidth: 8,
      },
      tooltip: {
        backgroundColor: "#1e293b",
        style: { color: "#f8fafc", fontSize: "11px" },
        borderWidth: 0,
        borderRadius: 6,
        headerFormat: "",
        pointFormatter() {
          return `<span style="color:${this.color}">●</span> <b>${this.series.name}</b><br/>${this.category} ${CURRENT_YEAR}: <b>${fullUsd(this.y)}</b>`;
        },
      },
      plotOptions: {
        spline: {
          lineWidth: 2,
          marker: { enabled: false, symbol: "circle", radius: 3 },
          states: { hover: { lineWidth: 3 } },
        },
        column: { borderRadius: 4, borderWidth: 0, pointPadding: 0.05, groupPadding: 0.12 },
        series: { connectNulls: true },
      },
      series: [
        {
          type: "column",
          name: totalBarLabel,
          data: monthlyTotals,
          color: "#16A34A",
          zIndex: 2,
        },
      ],
    };
  }, [series, categories, totalBarLabel, stacked]);

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h4 className="text-sm font-semibold text-gray-700">
            {isRevenue ? "Revenue" : "Expense"} Trend — {CURRENT_YEAR}
          </h4>
          <p className="text-[11px] text-gray-400 mt-0.5">
            {stacked
              ? "Monthly engineer + company expense · stacked · USD"
              : `Monthly total ${isRevenue ? "revenue" : "expense"} · USD`}
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-[400px]">
          <div className="w-7 h-7 border-[3px] border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : chartOptions ? (
        <BaseChart options={chartOptions} className="w-full" />
      ) : (
        <p className="text-sm text-gray-400 py-20 text-center">No expense data for {CURRENT_YEAR}.</p>
      )}
    </div>
  );
};

export default ExpenseYearTrendChart;
